import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  fetchVehicles,
  createVehicle,
  updateVehicle,
  deleteVehicle,
  getVehicleById,
  fetchProjectDocuments,
  getDocumentsByVehicle,
} from "../api/administrationService";

const VEHICLES_QUERY_KEY = ["vehicles"];

const normalizeVehicleResponse = (response) =>
  response?.data ?? response;

const getServiceProjectId = (service) =>
  Number(
    service?.serproid ??
    service?.SerProID ??
    service?.serProId
  );

const getProjectId = (service) =>
  Number(
    service?.proid ??
    service?.ProID
  );

export const useVehicles = () => {
  const queryClient =
    useQueryClient();

  const vehiclesQuery = useQuery({
    queryKey: VEHICLES_QUERY_KEY,
    queryFn: fetchVehicles,
    staleTime: 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: createVehicle,

    onSuccess: (response) => {
      const vehicle =
        normalizeVehicleResponse(
          response
        );

      queryClient.invalidateQueries({
        queryKey:
          VEHICLES_QUERY_KEY,
      });

      if (vehicle?.vehid) {
        queryClient.setQueryData(
          [
            "vehiculo",
            Number(vehicle.vehid),
          ],
          vehicle
        );
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }) =>
      updateVehicle(id, data),

    onSuccess: (
      response,
      variables
    ) => {
      const vehicle =
        normalizeVehicleResponse(
          response
        );

      const vehicleId =
        Number(variables.id);

      queryClient.invalidateQueries({
        queryKey:
          VEHICLES_QUERY_KEY,
      });

      queryClient.invalidateQueries({
        queryKey: [
          "vehiculo",
          vehicleId,
        ],
      });

      if (vehicle?.vehid) {
        queryClient.setQueryData(
          [
            "vehiculo",
            Number(vehicle.vehid),
          ],
          vehicle
        );
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteVehicle,

    onSuccess: (
      _response,
      vehicleId
    ) => {
      const id =
        Number(vehicleId);

      queryClient.invalidateQueries({
        queryKey:
          VEHICLES_QUERY_KEY,
      });

      queryClient.removeQueries({
        queryKey: [
          "vehiculo",
          id,
        ],
      });

      queryClient.removeQueries({
        queryKey: [
          "documentosVehiculo",
          id,
        ],
      });
    },
  });

  return {
    vehicles:
      vehiclesQuery.data ?? [],

    error:
      vehiclesQuery.error,

    isLoading:
      vehiclesQuery.isLoading,

    isFetching:
      vehiclesQuery.isFetching,

    refetch:
      vehiclesQuery.refetch,

    createMutation,
    updateMutation,
    deleteMutation,
  };
};

export const useVehiculoDetalle = (
  vehID
) => {
  const vehicleId =
    Number(vehID);

  const isValidVehicleId =
    Number.isInteger(vehicleId) &&
    vehicleId > 0;

  const vehiculoQuery = useQuery({
    queryKey: [
      "vehiculo",
      vehicleId,
    ],

    queryFn: () =>
      getVehicleById(vehicleId),

    enabled:
      isValidVehicleId,

    staleTime:
      60 * 1000,
  });

  const documentosVehiculoQuery =
    useQuery({
      queryKey: [
        "documentosVehiculo",
        vehicleId,
      ],

      queryFn: () =>
        getDocumentsByVehicle(
          vehicleId
        ),

      enabled:
        isValidVehicleId,
    });

  const vehiculo =
    vehiculoQuery.data;

  const serviciosProyecto =
    vehiculo?.serviciosProyecto ??
    vehiculo?.ServiciosProyecto ??
    [];

  /*
   * Derivar proyectos únicos desde los servicios
   * realmente asociados al vehículo.
   */
  const projectsMap =
    new Map();

  for (
    const service of serviciosProyecto
  ) {
    const projectId =
      getProjectId(service);

    if (
      !Number.isInteger(
        projectId
      ) ||
      projectId <= 0
    ) {
      continue;
    }

    if (
      !projectsMap.has(
        projectId
      )
    ) {
      projectsMap.set(
        projectId,
        {
          proid: projectId,

          pronom:
            service?.pronom ??
            service?.ProNom ??
            "Proyecto",
        }
      );
    }
  }

  const proyectos =
    Array.from(
      projectsMap.values()
    ).sort((a, b) =>
      String(a.pronom).localeCompare(
        String(b.pronom)
      )
    );

  return {
    vehiculo,
    serviciosProyecto,
    proyectos,

    documentosVehiculo:
      documentosVehiculoQuery.data ??
      [],

    isLoading:
      vehiculoQuery.isLoading ||
      documentosVehiculoQuery.isLoading,

    isFetching:
      vehiculoQuery.isFetching ||
      documentosVehiculoQuery.isFetching,

    error:
      vehiculoQuery.error ||
      documentosVehiculoQuery.error,

    fetchProjectDocuments,

    refetchVehiculo:
      vehiculoQuery.refetch,

    refetchDocumentosVehiculo:
      documentosVehiculoQuery.refetch,

    getServiceProjectId,
  };
};